import { Close } from "@mui/icons-material";
import { Box, Chip, css, IconButton, styled, Typography } from "@mui/material";
import { ChevronLeft } from "@procore/core-icons";
import { colors, GhostPlaceholder } from "@procore/core-react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetModelRevisionsQuery, useGetModelsQuery } from "apis/procore/procoreApi";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { selectIsLargeMapMobileOpen, setLargeMapMobileOpen } from "features/miniMap/minimapSlice";
import { useSceneId } from "hooks/useSceneId";
import { selectIsProcoreAndroid, selectProcoreContext } from "slices/explorer/selectors";

import { PROCORE_TOP_BAR, TOOLBAR_BUTTON, TOOLBAR_COLORS, TOOLBAR_CONTAINER } from "../constants";

const ControlsContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isPhonePortrait",
})<{ isPhonePortrait?: boolean }>`
    display: flex;
    align-items: ${({ isPhonePortrait }) => (isPhonePortrait ? "flex-start" : "center")};
    gap: 8px;
    /* In portrait, fill available width */
    ${({ isPhonePortrait }) => isPhonePortrait && "width: 100%;"}
`;

const ControlBox = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${TOOLBAR_CONTAINER.PADDING}px;
    background-color: ${TOOLBAR_CONTAINER.BG};
    border-radius: ${TOOLBAR_CONTAINER.RADIUS}px;
    border: ${TOOLBAR_CONTAINER.BORDER};
    box-shadow: ${TOOLBAR_CONTAINER.SHADOW};
`;

/** Close button box - 40x40 */
const CloseBox = styled(ControlBox)`
    position: relative;
    width: ${PROCORE_TOP_BAR.CLOSE_SIZE}px;
    height: ${PROCORE_TOP_BAR.CLOSE_SIZE}px;
    padding: 0;
    flex-shrink: 0;
`;

/** Merged dropdown box containing model name, revision, and status - horizontal layout */
const MergedDropdownBox = styled(ControlBox)`
    height: ${PROCORE_TOP_BAR.CLOSE_SIZE}px;
    overflow: hidden;

    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 10px 8px;
    border: none;
    color: ${TOOLBAR_COLORS.DEFAULT};
    font-size: 14px;

    svg {
        width: 18px;
        height: 18px;
        color: ${TOOLBAR_COLORS.DISABLED};
        flex-shrink: 0;
    }
`;

/** Vertical dropdown box for phone portrait - stacked name/revision, fills available space */
const VerticalDropdownBox = styled(ControlBox)`
    padding: 8px 12px;
    gap: 2px;
    flex: 1;
    min-width: 0; /* Allow flex shrinking */
`;

const closeControlStyles = `
    width: 100%;
    height: 100%;
    padding: 0;
    border-radius: ${TOOLBAR_BUTTON.RADIUS}px;
    background-color: transparent;
    color: ${TOOLBAR_COLORS.DEFAULT};
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    touch-action: manipulation;

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background-color: ${TOOLBAR_COLORS.HOVER};
        }
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

const CloseButton = styled(IconButton)`
    ${closeControlStyles}
`;

const CloseLink = styled("a")`
    ${closeControlStyles}
`;

/** Vertical dropdown button for phone portrait */
const VerticalDropdownButton = styled("div")(
    () => css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        padding: 0;
        border: none;
        background-color: transparent;
        color: ${TOOLBAR_COLORS.DEFAULT};
        text-align: left;
        width: 100%;
        min-width: 0; /* Allow flex shrinking */
    `,
);

const NameRow = styled(Box)`
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    min-width: 0; /* Allow flex shrinking */
`;

const StatusChip = styled(Chip)`
    height: 24px;
    font-size: 12px;
    font-weight: 500;
    background-color: ${TOOLBAR_COLORS.BADGE_BG};
    color: ${TOOLBAR_COLORS.BADGE_TEXT};
    flex-shrink: 0;
`;

interface SceneControlsProps {
    isPhonePortrait?: boolean;
}

/**
 * Scene controls - left side of top bar
 *
 * Normal mode: Close button + horizontal dropdown with name, revision, status
 * Phone portrait: Close button + vertical layout with name on top, revision below
 */
const PROCORE_ANDROID_CLOSE_URL = "procoremobile://app.procore.com/embedded_webview/close?reason=user_closed";

function StackedCloseControls({
    isProcoreAndroid,
    isLargeMapMobileOpen,
    onBackFromLargeMap,
    onClose,
}: {
    isProcoreAndroid: boolean;
    isLargeMapMobileOpen: boolean;
    onBackFromLargeMap: () => void;
    onClose: () => void;
}) {
    const exitLayerSx = {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: isLargeMapMobileOpen ? 0 : 1,
        visibility: (isLargeMapMobileOpen ? "hidden" : "visible") as "hidden" | "visible",
        pointerEvents: (isLargeMapMobileOpen ? "none" : "auto") as "none" | "auto",
    };

    return (
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            <Box sx={exitLayerSx} aria-hidden={isLargeMapMobileOpen}>
                {isProcoreAndroid ? (
                    <CloseLink href={PROCORE_ANDROID_CLOSE_URL} title="Close" data-action="exit">
                        <Close />
                    </CloseLink>
                ) : (
                    <CloseButton onClick={onClose} title="Close" data-action="exit" disableRipple disableFocusRipple>
                        <Close />
                    </CloseButton>
                )}
            </Box>
            {isLargeMapMobileOpen ? (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CloseButton
                        onClick={onBackFromLargeMap}
                        title="Back"
                        data-action="back"
                        disableRipple
                        disableFocusRipple
                        sx={{ width: "100%", height: "100%" }}
                    >
                        <ChevronLeft />
                    </CloseButton>
                </Box>
            ) : null}
        </Box>
    );
}

export function SceneControls({ isPhonePortrait }: SceneControlsProps) {
    const dispatch = useAppDispatch();
    const isProcoreAndroid = useAppSelector(selectIsProcoreAndroid);
    const isLargeMapMobileOpen = useAppSelector(selectIsLargeMapMobileOpen);
    const procoreContext = useAppSelector(selectProcoreContext);
    const sceneId = useSceneId();

    const { data: modelRevisionsData, isLoading: isModelRevisionsLoading } = useGetModelRevisionsQuery(
        procoreContext?.sceneVersionId && sceneId
            ? {
                  companyId: procoreContext.companyId,
                  projectId: procoreContext.projectId,
                  sceneId,
                  sceneVersionId: procoreContext.sceneVersionId,
                  latest: true,
              }
            : skipToken,
    );
    const modelRevision = modelRevisionsData?.[0];
    const modelRevisionLabel = modelRevision?.revision_number?.toString()?.padStart(2, "0");
    const modelRevisionStatus = modelRevision?.status_display;

    const { data: modelsData, isLoading: isModelsLoading } = useGetModelsQuery(
        procoreContext
            ? {
                  companyId: procoreContext.companyId,
                  projectId: procoreContext.projectId,
              }
            : skipToken,
    );
    const model = modelRevision ? modelsData?.data.find((m) => m.id === modelRevision.bim_model_id) : undefined;
    const modelLabel = model?.name;

    const isLoading = isModelRevisionsLoading || isModelsLoading;

    const handleClose = () => {
        console.log("Close clicked");
    };

    const handleBackFromLargeMap = () => {
        dispatch(setLargeMapMobileOpen(false));
    };

    // Phone portrait: vertical layout
    if (isPhonePortrait) {
        return (
            <ControlsContainer isPhonePortrait>
                <CloseBox>
                    <StackedCloseControls
                        isProcoreAndroid={isProcoreAndroid}
                        isLargeMapMobileOpen={isLargeMapMobileOpen}
                        onBackFromLargeMap={handleBackFromLargeMap}
                        onClose={handleClose}
                    />
                </CloseBox>

                {isLoading ? (
                    <VerticalDropdownBox>
                        <VerticalDropdownButton>
                            <Box sx={{ width: "140px" }}>
                                <GhostPlaceholder startGradientColor={colors.gray40} endGradientColor={colors.gray94} />
                            </Box>
                        </VerticalDropdownButton>
                    </VerticalDropdownBox>
                ) : modelLabel ? (
                    <VerticalDropdownBox>
                        <VerticalDropdownButton>
                            <NameRow>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                                >
                                    {modelLabel}
                                </Typography>
                                {/* <KeyboardArrowDown
                                    sx={{ width: 18, height: 18, color: TOOLBAR_COLORS.DISABLED, flexShrink: 0 }}
                                /> */}
                            </NameRow>
                            {modelRevision && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: TOOLBAR_COLORS.DEFAULT }}>
                                        Revision {modelRevisionLabel}
                                    </Typography>
                                    <StatusChip label={modelRevisionStatus} size="small" />
                                </Box>
                            )}
                        </VerticalDropdownButton>
                    </VerticalDropdownBox>
                ) : null}
            </ControlsContainer>
        );
    }

    return (
        <ControlsContainer>
            <CloseBox>
                <StackedCloseControls
                    isProcoreAndroid={isProcoreAndroid}
                    isLargeMapMobileOpen={isLargeMapMobileOpen}
                    onBackFromLargeMap={handleBackFromLargeMap}
                    onClose={handleClose}
                />
            </CloseBox>

            {isLoading ? (
                <MergedDropdownBox>
                    <Box sx={{ width: "140px" }}>
                        <GhostPlaceholder startGradientColor={colors.gray40} endGradientColor={colors.gray94} />
                    </Box>
                </MergedDropdownBox>
            ) : modelLabel ? (
                <MergedDropdownBox>
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        noWrap
                        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                        {modelLabel}
                    </Typography>
                    {modelRevision && (
                        <>
                            <Typography variant="caption" noWrap sx={{ color: TOOLBAR_COLORS.DEFAULT, flexShrink: 0 }}>
                                Revision {modelRevisionLabel}
                            </Typography>
                            <StatusChip label={modelRevisionStatus} size="small" />
                            {/* <KeyboardArrowDown /> */}
                        </>
                    )}
                </MergedDropdownBox>
            ) : null}
        </ControlsContainer>
    );
}
